import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Slider,
    Paper,
    IconButton,
    Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import BodyMap from "./BodyMap";
import BatteryLevel from "./BatteryLevel";
import {
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

export const drains = [
  { id: 'deadlines', icon: '⏰', label: 'Deadlines & Workload', category: 'performance', insight: 'Sustained cognitive demand and time pressure depleting mental reserves' },
  { id: 'meetings', icon: '📅', label: 'Back-to-Back Meetings', category: 'performance', insight: 'Decision fatigue and constant context-switching exhausting focus' },
  { id: 'boss', icon: '👔', label: 'Manager Pressure', category: 'emotional', insight: 'Authority-related stress activating threat-response patterns' },
  { id: 'team', icon: '👥', label: 'Team Dynamics', category: 'emotional', insight: 'Interpersonal friction consuming emotional bandwidth' },
  { id: 'commute', icon: '🚗', label: 'Commute / Hybrid Fatigue', category: 'physical', insight: 'Transit stress and environment-switching draining energy' },
  { id: 'sleep_d', icon: '😴', label: 'Poor Sleep', category: 'recovery', insight: 'Sleep deficit — battery cannot fully recharge overnight' },
  { id: 'screen', icon: '💻', label: 'Screen Fatigue', category: 'stimulation', insight: 'Extended screen time depleting attention and causing eye strain' },
  { id: 'money', icon: '💰', label: 'Financial Worry', category: 'psychological', insight: 'Financial concerns consuming background mental processing' },
  { id: 'balance', icon: '⚖️', label: 'Work-Life Imbalance', category: 'psychological', insight: 'Boundary erosion leaving no space for recovery and recharge' },
  { id: 'recog', icon: '🏆', label: 'Lack of Recognition', category: 'connection', insight: 'Effort without acknowledgement draining motivation and purpose' }
];

const AssessmentFlow = () => {
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [battery, setBattery] = useState(50);
    const [bodyData, setBodyData] = useState({});
    const [selected, setSelected] = useState([]);

    console.log("selected", selected)

    const toggleDrain = (item) => {
        setSelected((prev) =>
            prev.includes(item)
                ? prev.filter((i) => i !== item)
                : [...prev, item]
        );
    };

    return (
        <Box sx={{
            background: "#f3f4f6",
            minHeight: {
                xs: 'calc(100vh - 56px)',
                sm: 'calc(100vh - 64px)',
                md: 'calc(100vh - 70px)',
            },
        }}>
            <Box
                sx={{
                    // py: 4,
                    background: "#f3f4f6",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    px: 2,
                    position: "relative"
                }}
            >
                <Box display="flex" justifyContent="space-between" gap={1} mb={2} width="100%">
                    <Box sx={{ mt: 2, }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 700, background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        }}>
                            Wellness Assessment
                        </Typography>
                    </Box>

                    <IconButton
                        onClick={() => navigate("/echo-sounds")}
                        sx={{
                            backgroundColor: '#F0F5FF',
                            width: { xs: 32, sm: 35 },
                            height: { xs: 32, sm: 35 },
                            mt: 2,
                            borderRadius: '50%',
                            color: '#0B2D6B',
                            position: "absolute",
                            top: 0,
                            right: { xs: 5, sm: 10 },
                            '&:hover': {
                                backgroundColor: '#E0EBFF'
                            }
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                </Box>

                {/* 🔥 PROGRESS */}
                <Box display="flex" gap={1} mb={2}>
                    {[1, 2, 3].map((s) => (
                        <Box
                            key={s}
                            sx={{
                                flex: 1,
                                height: 5,
                                borderRadius: 2,
                                background:
                                    step === s
                                        ? "linear-gradient(90deg,#26A69A,#7E57C2)"
                                        : step > s
                                            ? "#26A69A"
                                            : "#e5e7eb",
                            }}
                        />
                    ))}
                </Box>

                {/* 🔥 STEP LABEL */}
                <Typography
                    fontSize={{ xs: 11, sm: 12 }}
                    fontWeight={700}
                    color="#6b7280"
                    mb={1}
                >
                    Part {step} —{" "}
                    {step === 1 && "Body Scan"}
                    {step === 2 && "Energy Level"}
                    {step === 3 && "What's Draining You"}
                </Typography>

                {/* 🔥 CARD */}
                <Paper
                    sx={{
                        borderRadius: "20px",
                        p: { xs: 2, sm: 2.5 },
                        border: "1px solid #e5e7eb",
                        boxShadow: "none",
                        width: {
                            xs: '100%',
                            sm: '90%',
                            md: 'auto',
                        },
                        maxWidth: 500,
                    }}
                >
                    {/* STEP 1 */}
                    {step === 1 && (
                        <>
                            <Typography
                                fontWeight={700}
                                fontSize={{ xs: 16, sm: 18 }}
                                fontFamily="Georgia, serif"
                            >
                                Where do you feel tension?
                            </Typography>

                            <Typography
                                color="text.secondary"
                                fontSize={{ xs: 12, sm: 13 }}
                                mt={1}
                            >
                                Tap body areas: 1st tap = mild, 2nd = moderate, 3rd = intense, 4th = clear. Skip areas that feel fine.
                            </Typography>

                            <BodyMap
                                selected={bodyData}
                                setSelected={setBodyData}
                            />
                        </>
                    )}

                    {/* STEP 2 */}
                    {step === 2 && (
                        <Box sx={{ width: { xs: '100%', sm: '460px' } }}>
                            <Typography fontWeight={700} fontSize={{ xs: 16, sm: 18 }}>
                                How full is your mental battery?

                            </Typography>
                            <Typography color="text.secondary" fontSize={{ xs: 12, sm: 13 }}>
                                Drag the slider to where you feel you are right now.
                            </Typography>

                            <Box textAlign="center" my={3}>

                                <BatteryLevel
                                    value={battery}
                                    setValue={setBattery}
                                />
                            </Box>


                        </Box>
                    )}

                    {/* STEP 3 */}
                    {step === 3 && (
                        <Box sx={{ width: { xs: '100%', sm: '460px' } }}>
                            <Typography fontWeight={700} fontSize={{ xs: 16, sm: 18 }}>
                                What's consuming your energy?
                            </Typography>
                            <Typography color="text.secondary" fontSize={{ xs: 12, sm: 13 }}>
                                Tap all that apply — or skip if you're fully charged!
                            </Typography>

                            <Box
                                display="flex"
                                flexWrap="wrap"
                                gap={2}
                                // justifyContent="center"
                                mt={2}
                            >
                                {drains.map((d) => {
                                   const isSelected = selected.includes(d.id);

                                    return (
                                        <Box
                                            key={d.label}
                                           onClick={() => toggleDrain(d.id)}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                px: 2.5,
                                                py: 1.2,
                                                borderRadius: "999px",
                                                cursor: "pointer",

                                                // ✅ DEFAULT STYLE
                                                border: "2px solid #e5e7eb",
                                                background: "#f9fafb",

                                                // ✅ SELECTED STYLE (AFTER CLICK)
                                                ...(isSelected && {
                                                    border: "2px solid #0ea5a4",
                                                    background: "#f0fdfa",
                                                }),

                                                // ✅ HOVER EFFECT
                                                "&:hover": {
                                                    border: "2px solid #0ea5a4",
                                                    background: "#f0fdfa",
                                                },

                                                transition: "all 0.2s ease",
                                            }}
                                        >
                                            <Box fontSize={18}>{d.icon}</Box>

                                            <Typography
                                                fontSize={14}
                                                fontWeight={500}
                                                color="#374151"
                                            >
                                                {d.label}
                                            </Typography>
                                        </Box>
                                    );
                                })}

                               

                            </Box>
  <Typography
  mt={2}
  color="#6b7280"
  textAlign="center"
  width="100%"
>
  {selected.length === 0
    ? "No drains selected"
    : `${selected.length} ${
        selected.length === 1 ? "drain" : "drains"
      } selected`}
</Typography>
                        </Box>
                    )}
                </Paper>

                {/* 🔥 BUTTONS */}
                <Box display="flex" gap={{ xs: 1.5, sm: 2 }} mt={1} flexDirection={{ xs: 'column', sm: 'row' }} width={{ xs: '100%', sm: 'auto' }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() =>
                            step === 1
                                ? navigate("/decode-stress")
                                : setStep(step - 1)
                        }
                        sx={{
                            width: { xs: '100%', sm: '240px' },
                            height: "50px",
                            borderRadius: "16px",
                            textTransform: "none",
                            fontWeight: 550,
                            background: "#f3f4f6cf",
                            color: "#757f8e",
                            border: "1px solid #e5e7eb",
                            "&:hover": {
                                background: "#e5e7eb",
                            },
                        }}
                    >
                        Back
                    </Button>

                    <Button
                        fullWidth
                        sx={{
                            width: { xs: '100%', sm: 240 },
                            py: 1.6,
                            borderRadius: "14px",
                            fontWeight: "bold",
                            color: "#ffffffd6",
                            fontSize: 14,
                            background: "linear-gradient(90deg, #26A69A, #7E57C2)",
                            "&:hover": {
                                background: "linear-gradient(90deg, #1e8e88, #6a45c2)",
                            },
                        }}
                        onClick={() => {
                            if (step < 3) {
                                setStep(step + 1);
                            } else {
                                navigate("/result", {
                                    state: { bodyData, battery, selectedDrains: selected },
                                });
                            }
                        }}
                    >
                        {step === 1 && "Next — Energy Battery"}
                        {step === 2 && "Next — Energy Drains"}
                        {step === 3 && "See My Results"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default AssessmentFlow; 
